import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { UserCollection } from '../../../../../models/users/user-collection.model';
import { UserCollectionAlbum } from '../../../../../models/users/user-collection-album.model';
import { UserCollectionAlbumCard } from '../../../../../models/users/user-collection-album-card.model';
import { ApiUsersService } from '../../../../../services/users/api/api-users.service';
import { AlertsService } from '../../../../../services/alerts.service';
import { DateService } from '../../../../../services/date.service';
import { CollectionsImagesHelper } from '../../../../../utils/helpers/collections-images.helper';

type UserCollectionsView = 'collections' | 'albums' | 'cards';

interface UserCollectionsPagination {
  pageNumber: number;
  totalResults: number;
}

@Component({
  selector: 'app-user-collections-tab',
  templateUrl: './user-collections-tab.component.html',
  standalone: true,
  imports: [CommonModule, TooltipModule]
})
export class UserCollectionsTabComponent {

  @Input() userId: number = 0;

  view: UserCollectionsView = 'collections';
  isLoading: boolean = false;
  updatingCardId: number = 0;
  resultsPerPage: number = 25;

  collections: UserCollection[] = [];
  albums: UserCollectionAlbum[] = [];
  cards: UserCollectionAlbumCard[] = [];

  selectedCollection: UserCollection | null = null;
  selectedAlbum: UserCollectionAlbum | null = null;

  brokenImageCardIds: Set<number> = new Set<number>();

  paginations: Record<UserCollectionsView, UserCollectionsPagination> = {
    collections: { pageNumber: 1, totalResults: 0 },
    albums: { pageNumber: 1, totalResults: 0 },
    cards: { pageNumber: 1, totalResults: 0 }
  };

  get pagination(): UserCollectionsPagination {
    return this.paginations[this.view];
  }

  get currentCount(): number {
    switch (this.view) {
      case 'albums': return this.albums.length;
      case 'cards': return this.cards.length;
      default: return this.collections.length;
    }
  }

  get totalPages(): number {
    return Math.ceil(this.pagination.totalResults / this.resultsPerPage);
  }

  getPageNumbers(): (number | null)[] {
    const total = this.totalPages;
    const current = this.pagination.pageNumber;

    const visibleSet = new Set<number>();
    for (let i = 1; i <= Math.min(2, total); i++) visibleSet.add(i);
    for (let i = Math.max(total - 1, 1); i <= total; i++) visibleSet.add(i);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) visibleSet.add(i);

    const sorted = Array.from(visibleSet).sort((a, b) => a - b);

    const result: (number | null)[] = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
      result.push(sorted[i]);
    }

    return result;
  }

  constructor(public dateService: DateService, private apiUsersService: ApiUsersService, private alertsService: AlertsService) {}

  load(): void {
    this.view = 'collections';
    this.selectedCollection = null;
    this.selectedAlbum = null;
    this.paginations.collections.pageNumber = 1;
    this.refreshList();
  }

  goToPage(page: number): void {
    this.pagination.pageNumber = page;
    this.refreshList();
  }

  onClickPaginationPrevious(): void {
    this.pagination.pageNumber--;
    this.refreshList();
  }

  onClickPaginationNext(): void {
    this.pagination.pageNumber++;
    this.refreshList();
  }

  onClickCollectionDetails(collection: UserCollection): void {
    this.selectedCollection = collection;
    this.albums = [];
    this.view = 'albums';
    this.paginations.albums.pageNumber = 1;
    this.refreshList();
  }

  onClickAlbumDetails(album: UserCollectionAlbum): void {
    this.selectedAlbum = album;
    this.cards = [];
    this.view = 'cards';
    this.paginations.cards.pageNumber = 1;
    this.refreshList();
  }

  onClickBack(): void {
    if (this.view === 'cards') {
      this.selectedAlbum = null;
      this.view = 'albums';
    } else if (this.view === 'albums') {
      this.selectedCollection = null;
      this.view = 'collections';
    }
    this.refreshList();
  }

  onClickIncrementCard(card: UserCollectionAlbumCard): void {
    this.updatingCardId = card.cardId;
    this.apiUsersService.addUserCollectionAlbumCard(this.userId, card.cardId).then(
      () => {
        this.updatingCardId = 0;
        card.cardsCount++;
      }, error => {
        this.updatingCardId = 0;
        this.alertsService.displayAlertError('Error adding card : ' + error);
      }
    );
  }

  onClickDecrementCard(card: UserCollectionAlbumCard): void {
    if (card.cardsCount <= 0) return;
    this.updatingCardId = card.cardId;
    this.apiUsersService.removeUserCollectionAlbumCard(this.userId, card.cardId).then(
      () => {
        this.updatingCardId = 0;
        card.cardsCount--;
      }, error => {
        this.updatingCardId = 0;
        this.alertsService.displayAlertError('Error removing card : ' + error);
      }
    );
  }

  hasImage(card: UserCollectionAlbumCard): boolean {
    return !this.brokenImageCardIds.has(card.cardId);
  }

  getImageUrl(card: UserCollectionAlbumCard): string {
    return CollectionsImagesHelper.getImageUrl('card', card.cardId, null);
  }

  onImageError(card: UserCollectionAlbumCard): void {
    this.brokenImageCardIds.add(card.cardId);
  }

  private refreshList(): void {
    switch (this.view) {
      case 'albums': this.refreshAlbums(); break;
      case 'cards': this.refreshCards(); break;
      default: this.refreshCollections(); break;
    }
  }

  private refreshCollections(): void {
    this.isLoading = true;
    const pagination = this.paginations.collections;

    this.apiUsersService.getUserCollections(this.userId, pagination.pageNumber, this.resultsPerPage).then(
      response => {
        this.isLoading = false;
        this.collections = response.results;
        pagination.totalResults = response.resultsTotal;
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error loading collections (' + error + ')');
      }
    );
  }

  private refreshAlbums(): void {
    if (!this.selectedCollection) return;
    this.isLoading = true;
    const pagination = this.paginations.albums;

    this.apiUsersService.getUserCollectionAlbums(this.userId, this.selectedCollection.collectionId, pagination.pageNumber, this.resultsPerPage).then(
      response => {
        this.isLoading = false;
        this.albums = response.results;
        pagination.totalResults = response.resultsTotal;
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error loading albums (' + error + ')');
      }
    );
  }

  private refreshCards(): void {
    if (!this.selectedAlbum) return;
    this.isLoading = true;
    const pagination = this.paginations.cards;

    this.apiUsersService.getUserCollectionAlbumCards(this.userId, this.selectedAlbum.albumId, pagination.pageNumber, this.resultsPerPage).then(
      response => {
        this.isLoading = false;
        this.cards = response.results;
        pagination.totalResults = response.resultsTotal;
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error loading cards (' + error + ')');
      }
    );
  }
}
